/**
 * NotificationTemplatesPage — Tasks & Templates · Notification Templates.
 *
 * Every email / Teams ping / SMS / printed letter the platform sends. Edit the
 * wording once here and every event that resolves to this template code picks it
 * up — no code change, no redeploy. The dispatcher resolves a template by CODE
 * (from the Event Binding's defaultTemplateCode), renders the ACTIVE version's
 * Handlebars body, and fans out per the recipient resolver + audience.
 *
 * Contract (verified against the backend 2026-06-16):
 *   - Read:  GET /v3/config/notification-templates — composite envelope
 *            { templates, versions, channels, categories, variables, routingRules }.
 *            The typed config.notificationTemplates() envelope is aspirational and
 *            does NOT match the wire (entity rows: code/channelCode/audience/…), so
 *            we read raw with the real shape — same as the source editor.
 *   - Write: POST /v3/admin/notification-templates — metadata + active-version
 *            content. audience/pillar/pillarTriggerKey were a silent no-op until
 *            2026-06-16 (write DTO dropped them); the backend now persists them
 *            (AdminNotificationsService.UpsertTemplateAsync), so these controls
 *            are live. Audience drives the dispatcher's audience-typed fan-out;
 *            Pillar feeds Reports/Metrics KPIs.
 *   - Delete: soft (re-upsert IsActive=false) — runtime stops sending; row kept.
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL → buildAdminAuthHeaders omits the bypass-key
 * (MSAL bearer carries authority; no secret in the bundle).
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, TextArea, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface TemplateRow {
  code: string;
  displayName: string;
  description: string | null;
  channelCode: string;
  categoryCode: string;
  activeVersionId: number | null;
  isActive: boolean;
  audience?: string | null;
  pillar?: string | null;
  pillarTriggerKey?: string | null;
}

interface VersionRow {
  versionId: number;
  templateCode: string;
  versionNumber: string;
  subject: string | null;
  bodyHandlebars: string;
  plainTextAlt: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  authoredBy: string;
}

interface ChannelRow {
  code: string;
  name: string;
}
interface CategoryRow {
  code: string;
  name: string;
}

interface EditDraft {
  code: string;
  displayName: string;
  description: string;
  channelCode: string;
  categoryCode: string;
  isActive: boolean;
  audience: string;
  pillar: string;
  pillarTriggerKey: string;
  versionNumber: string;
  subject: string;
  body: string;
  isNew: boolean;
}

const AUDIENCE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'customer', label: 'Customer' },
  { value: 'fsr-officer', label: 'FSR / Officer' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'system-ops', label: 'System Ops' },
];

const PILLAR_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'cross-selling', label: 'Cross-selling' },
  { value: 'nurturing', label: 'Nurturing' },
  { value: 'servicing', label: 'Servicing' },
];

const COMMON_TOKENS: ReadonlyArray<{ token: string; sample: string }> = [
  { token: '{{signer.fullName}}', sample: 'Jane A. Doe' },
  { token: '{{signer.firstName}}', sample: 'Jane' },
  { token: '{{customer.email}}', sample: 'jane@example.com' },
  { token: '{{cart.id}}', sample: 'CART-2026-0042' },
  { token: '{{appointment.date}}', sample: 'May 5 at 10:00 AM' },
  { token: '{{officer.name}}', sample: 'Bill Wiggins' },
  { token: '{{branch.name}}', sample: 'DTN Rolla' },
  { token: '{{bank.legalName}}', sample: 'Phelps County Bank' },
  { token: '{{bank.compliancePhone}}', sample: '573-364-5202' },
  { token: '{{signing.url}}', sample: 'https://sign.pcb.bank/abc' },
];

function emptyDraft(channelCode: string, categoryCode: string): EditDraft {
  return {
    code: '',
    displayName: '',
    description: '',
    channelCode,
    categoryCode,
    isActive: true,
    audience: 'fsr-officer',
    pillar: 'servicing',
    pillarTriggerKey: '',
    versionNumber: '1.0',
    subject: '',
    body: '',
    isNew: true,
  };
}

/** Substitute the known sample tokens so the preview feels real (no Handlebars at build time). */
function renderPreview(body: string): string {
  let out = body;
  for (const t of COMMON_TOKENS) out = out.split(t.token).join(t.sample);
  return out;
}

function labelFor(value: string | null | undefined, opts: ReadonlyArray<{ value: string; label: string }>): string {
  if (!value) return '—';
  return opts.find((o) => o.value === value)?.label ?? value;
}

export function NotificationTemplatesPage(): JSX.Element {
  const client = useApiClient();
  const [templates, setTemplates] = useState<ReadonlyArray<TemplateRow> | null>(null);
  const [versions, setVersions] = useState<ReadonlyArray<VersionRow>>([]);
  const [channels, setChannels] = useState<ReadonlyArray<ChannelRow>>([]);
  const [categories, setCategories] = useState<ReadonlyArray<CategoryRow>>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [draftActiveVersionId, setDraftActiveVersionId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TemplateRow | null>(null);
  const [channelFilter, setChannelFilter] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('');
  const [pillarFilter, setPillarFilter] = useState('');
  const [groupBy, setGroupBy] = useState<'none' | 'audience' | 'pillar'>('none');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setTemplates(null);
      setLoadError(null);
      try {
        const resp = await client.http.get<{
          success: boolean;
          data?: {
            templates: ReadonlyArray<TemplateRow>;
            versions: ReadonlyArray<VersionRow>;
            channels: ReadonlyArray<ChannelRow>;
            categories: ReadonlyArray<CategoryRow>;
          };
          error?: string;
        }>('/v3/config/notification-templates');
        if (cancelled) return;
        if (resp.success && resp.data) {
          setTemplates(resp.data.templates ?? []);
          setVersions(resp.data.versions ?? []);
          setChannels(resp.data.channels ?? []);
          setCategories(resp.data.categories ?? []);
        } else {
          setLoadError(resp.error ?? 'Failed to load notification templates.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const channelOptions = useMemo(
    () => [{ value: '', label: 'All channels' }, ...channels.map((c) => ({ value: c.code, label: c.name }))],
    [channels],
  );

  // Latest version per template (by effectiveFrom) — the body the editor authors.
  const versionByTemplate = useMemo(() => {
    const map: Record<string, VersionRow | undefined> = {};
    for (const v of versions) {
      const existing = map[v.templateCode];
      if (!existing || new Date(v.effectiveFrom) > new Date(existing.effectiveFrom)) map[v.templateCode] = v;
    }
    return map;
  }, [versions]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (templates ?? []).filter((t) => {
      if (channelFilter && t.channelCode !== channelFilter) return false;
      if (audienceFilter && (t.audience ?? '') !== audienceFilter) return false;
      if (pillarFilter && (t.pillar ?? '') !== pillarFilter) return false;
      if (needle) {
        const hay = `${t.code} ${t.displayName} ${t.description ?? ''} ${t.pillarTriggerKey ?? ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [templates, channelFilter, audienceFilter, pillarFilter, search]);

  const grouped = useMemo(() => {
    if (groupBy === 'none') return null;
    const opts = groupBy === 'audience' ? AUDIENCE_OPTIONS : PILLAR_OPTIONS;
    const map = new Map<string, TemplateRow[]>();
    for (const t of visible) {
      const raw = (groupBy === 'audience' ? t.audience : t.pillar) ?? '';
      const key = raw ? labelFor(raw, opts) : '— Unset —';
      const arr = map.get(key) ?? [];
      arr.push(t);
      map.set(key, arr);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [visible, groupBy]);

  const openAdd = useCallback(() => {
    setDraft(emptyDraft(channels[0]?.code ?? 'email', categories[0]?.code ?? 'transactional'));
    setDraftActiveVersionId(null);
    setSaveError(null);
  }, [channels, categories]);

  const openEdit = useCallback(
    (t: TemplateRow) => {
      const v = versionByTemplate[t.code];
      setDraft({
        code: t.code,
        displayName: t.displayName,
        description: t.description ?? '',
        channelCode: t.channelCode,
        categoryCode: t.categoryCode,
        isActive: t.isActive,
        audience: t.audience ?? 'fsr-officer',
        pillar: t.pillar ?? 'servicing',
        pillarTriggerKey: t.pillarTriggerKey ?? '',
        versionNumber: v?.versionNumber ?? '1.0',
        subject: v?.subject ?? '',
        body: v?.bodyHandlebars ?? '',
        isNew: false,
      });
      setDraftActiveVersionId(t.activeVersionId);
      setSaveError(null);
    },
    [versionByTemplate],
  );

  const upsert = useCallback(
    async (d: EditDraft, activeVersionId: number | null): Promise<boolean> => {
      const resp = await client.http.post<{ success: boolean; error?: string }>(
        '/v3/admin/notification-templates',
        {
          headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
          body: {
            code: d.code.trim(),
            displayName: d.displayName.trim(),
            description: d.description.trim() ? d.description.trim() : null,
            channelCode: d.channelCode,
            categoryCode: d.categoryCode,
            isActive: d.isActive,
            audience: d.audience,
            pillar: d.pillar,
            pillarTriggerKey: d.pillarTriggerKey.trim() ? d.pillarTriggerKey.trim() : null,
            activeVersionId,
            versionNumber: d.versionNumber.trim() || '1.0',
            subject: d.subject.trim() ? d.subject.trim() : null,
            bodyHandlebars: d.body,
          },
        },
      );
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
      return true;
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.code.trim() || !draft.displayName.trim()) {
      setSaveError('Template code and friendly name are required.');
      return;
    }
    if (!draft.body.trim()) {
      setSaveError('The body cannot be empty.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(draft, draftActiveVersionId);
      setDraft(null);
      setToast({ tone: 'info', text: `Saved. ${draft.displayName} is updated.` });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, draftActiveVersionId, upsert, refresh]);

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const v = versionByTemplate[deleteTarget.code];
    const inactive: EditDraft = {
      code: deleteTarget.code,
      displayName: deleteTarget.displayName,
      description: deleteTarget.description ?? '',
      channelCode: deleteTarget.channelCode,
      categoryCode: deleteTarget.categoryCode,
      isActive: false,
      audience: deleteTarget.audience ?? 'fsr-officer',
      pillar: deleteTarget.pillar ?? 'servicing',
      pillarTriggerKey: deleteTarget.pillarTriggerKey ?? '',
      versionNumber: v?.versionNumber ?? '1.0',
      subject: v?.subject ?? '',
      body: v?.bodyHandlebars ?? '',
      isNew: false,
    };
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(inactive, deleteTarget.activeVersionId);
      setToast({ tone: 'warn', text: `${deleteTarget.displayName} is now inactive. The runtime will stop sending it.` });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, versionByTemplate, upsert, refresh]);

  const columns: ReadonlyArray<DataTableColumn<TemplateRow>> = useMemo(
    () => [
      { header: 'Code', cell: (t) => <code className="cell-mono--bold">{t.code}</code> },
      {
        header: 'Friendly name',
        cell: (t) => (
          <span>
            <span className="cell-strong">{t.displayName}</span>
            {t.description && <div className="cell-desc">{t.description}</div>}
          </span>
        ),
      },
      { header: 'Channel', cell: (t) => <Pill tone="info">{t.channelCode}</Pill> },
      { header: 'Audience', cell: (t) => <span className="cell-desc">{labelFor(t.audience, AUDIENCE_OPTIONS)}</span> },
      { header: 'Pillar', cell: (t) => <span className="cell-desc">{labelFor(t.pillar, PILLAR_OPTIONS)}</span> },
      {
        header: 'Trigger key',
        cell: (t) => (t.pillarTriggerKey ? <code className="cell-mono">{t.pillarTriggerKey}</code> : <span className="cell-desc">—</span>),
      },
      { header: 'Active', cell: (t) => (t.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>) },
      {
        header: '',
        cell: (t) => (
          <div className="card-actions-row">
            <Button onClick={() => openEdit(t)}>Edit</Button>
            {t.isActive && <Button onClick={() => setDeleteTarget(t)}>Deactivate</Button>}
          </div>
        ),
      },
    ],
    [openEdit],
  );

  const previewBody = draft ? renderPreview(draft.body) : '';
  const previewSubject = draft ? renderPreview(draft.subject) : '';

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load notification templates: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!templates) return <Spinner label="Loading notification templates…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Notification Templates — ${visible.length} of ${templates.length}`}
        actions={
          <Button variant="primary" onClick={openAdd}>
            + Add Template
          </Button>
        }
      >
        <div className="filter-grid">
          <SelectField label="Channel" value={channelFilter} onChange={setChannelFilter} options={channelOptions} />
          <SelectField
            label="Audience"
            value={audienceFilter}
            onChange={setAudienceFilter}
            options={[{ value: '', label: 'All audiences' }, ...AUDIENCE_OPTIONS]}
          />
          <SelectField
            label="Pillar"
            value={pillarFilter}
            onChange={setPillarFilter}
            options={[{ value: '', label: 'All pillars' }, ...PILLAR_OPTIONS]}
          />
          <SelectField
            label="Group by"
            value={groupBy}
            onChange={(v) => setGroupBy(v as 'none' | 'audience' | 'pillar')}
            options={[
              { value: 'none', label: 'No grouping' },
              { value: 'audience', label: 'Audience' },
              { value: 'pillar', label: 'Pillar' },
            ]}
          />
          <TextField label="Search" value={search} onChange={setSearch} placeholder="code, name, description, trigger…" />
        </div>

        {grouped === null ? (
          <DataTable<TemplateRow>
            columns={columns}
            rows={visible}
            rowKey={(t) => t.code}
            emptyState={<p className="empty">No templates match the current filters.</p>}
          />
        ) : (
          grouped.map(([groupLabel, rows]) => (
            <Fragment key={groupLabel}>
              <p className="group-heading">{groupLabel} ({rows.length})</p>
              <DataTable<TemplateRow> columns={columns} rows={rows} rowKey={(t) => t.code} />
            </Fragment>
          ))
        )}
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add a notification template' : `Edit ${draft?.displayName ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={760}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="Template code"
                value={draft.code}
                onChange={(v) => draft.isNew && setDraft({ ...draft, code: v.slice(0, 64) })}
                disabled={!draft.isNew}
                monospace
                placeholder="e.g. signing.invite"
                hint={draft.isNew ? 'Up to 64 chars. Permanent.' : 'Permanent.'}
              />
              <TextField
                label="Friendly name"
                value={draft.displayName}
                onChange={(v) => setDraft({ ...draft, displayName: v })}
                placeholder="e.g. Signing Invitation"
              />
            </div>

            <TextField
              label="Description"
              value={draft.description}
              onChange={(v) => setDraft({ ...draft, description: v })}
              placeholder="One-line note about when this template fires."
            />

            <div className="form-row">
              <SelectField
                label="Channel"
                value={draft.channelCode}
                onChange={(v) => setDraft({ ...draft, channelCode: v })}
                options={channels.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }))}
              />
              <SelectField
                label="Category"
                value={draft.categoryCode}
                onChange={(v) => setDraft({ ...draft, categoryCode: v })}
                options={categories.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }))}
                hint="Drives compliance handling (CAN-SPAM / GLBA)."
              />
            </div>

            <div className="form-row">
              <SelectField
                label="Audience"
                value={draft.audience}
                onChange={(v) => setDraft({ ...draft, audience: v })}
                options={AUDIENCE_OPTIONS}
                hint="Who this template addresses. Drives the dispatcher's audience-typed fan-out."
              />
              <SelectField
                label="Pillar"
                value={draft.pillar}
                onChange={(v) => setDraft({ ...draft, pillar: v })}
                options={PILLAR_OPTIONS}
                hint="Engagement bucket. Feeds Reports/Metrics pillar KPIs."
              />
            </div>

            <div className="form-row">
              <TextField
                label="Pillar trigger key"
                value={draft.pillarTriggerKey}
                onChange={(v) => setDraft({ ...draft, pillarTriggerKey: v.slice(0, 64) })}
                monospace
                placeholder="e.g. day-1-welcome (optional)"
              />
              <TextField
                label="Version number"
                value={draft.versionNumber}
                onChange={(v) => setDraft({ ...draft, versionNumber: v.slice(0, 16) })}
                placeholder="1.0"
                hint="Bump when you make a meaningful change."
              />
            </div>

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint={draft.isActive ? 'Runtime sends it.' : 'Paused — runtime skips it.'}
            />

            {draft.channelCode === 'email' && (
              <TextField
                label="Subject (email only)"
                value={draft.subject}
                onChange={(v) => setDraft({ ...draft, subject: v })}
                placeholder="e.g. Please sign your account-opening package"
              />
            )}

            <div>
              <span className="form-field__label">Insert a placeholder</span>
              <div className="token-row">
                {COMMON_TOKENS.map((t) => (
                  <button
                    key={t.token}
                    type="button"
                    className="token-chip"
                    onClick={() => setDraft({ ...draft, body: draft.body + t.token })}
                  >
                    {t.token}
                  </button>
                ))}
              </div>
            </div>

            <TextArea
              label="Body"
              value={draft.body}
              onChange={(v) => setDraft({ ...draft, body: v })}
              rows={10}
              placeholder="<p>Hi {{signer.firstName}}, please sign at {{signing.url}}.</p>"
            />

            <div className="preview-pane">
              <div className="preview-pane__label">Preview (with sample placeholder values)</div>
              {draft.channelCode === 'email' && draft.subject && (
                <div className="preview-pane__subject">Subject: {previewSubject}</div>
              )}
              {/* Admin-authored content with fixed-sample token substitution;
                  nothing user-supplied at runtime reaches this surface. */}
              <div className="preview-pane__body" dangerouslySetInnerHTML={{ __html: previewBody }} />
            </div>
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Mark this template inactive?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleSoftDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Mark inactive'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We'll set <strong>{deleteTarget.displayName}</strong> (
              <code className="cell-mono">{deleteTarget.code}</code>) to inactive. The runtime stops
              sending it, but the row + version history stay for audit. Flip back on anytime.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
